// Token-AND substring matching, ranking and highlight splitting for global
// search (#1715). React-free and dependency-free so the Cloud Run api, both
// SPAs and the unit tests all run the identical rules — a result the server
// ranked first must render first, and the <mark> the client draws must sit on
// the substring the server matched.
//
// This is the promotion the two existing copies asked for: settingsSearch.js
// ("Promote to @embertracking/ui if a third consumer shows up") and
// gear/redesign/gearSearch.js each carry their own two-line tokenize/matches
// pair. They stay where they are — both match a fixed in-memory list and neither
// ranks — but every new consumer should start here.
//
// Nothing in functions/ imports this, so changes need no npm publish. Per
// common/CLAUDE.md this file must never import the package's CJS by relative
// path (a Vite dev server would serve the CJS raw to the browser); it imports
// nothing at all.

// Tokens shorter than this are dropped, so "e51 in" searches for "e51" rather
// than returning every record containing the letters "in".
const MIN_TOKEN_LENGTH = 2;

// Weights. The ordering the issue specifies — exact title > title prefix >
// title substring > body substring — falls out of these being strictly
// separated, so no number of body hits can outrank a title hit.
const WEIGHT = {
    titleExact: 24,
    titlePrefix: 14,
    titleWord: 10,
    titleSubstring: 7,
    subtitle: 4,
    typeLabel: 2,
    field: 2,
    // A single field can only contribute so much: an incident whose narrative
    // says "wong" nine times is not nine times the answer to "wong".
    fieldCap: 6
};

// Recency is a TIEBREAK, not a ranking input — it can only reorder entries whose
// text scores are equal. Kept far below the smallest weight gap so a newer weak
// match can never climb over an older strong one.
const RECENCY_MAX = 0.9;
const RECENCY_HALF_LIFE_DAYS = 365;

// A token that only matched after its plural was stripped scores slightly under
// one that matched as typed, so "Qualifications" still beats "Qualification" for
// the query "qualifications". Small enough that it only ever separates results
// inside the same weight tier — it can't drop a title match below a body one.
const INFLECTED_SCALE = 0.9;

/** Split a raw query into lowercased tokens, dropping 1-character noise. */
export const tokenize = query => String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(token => token.length >= MIN_TOKEN_LENGTH);

// Plural → singular, so "qualifications" finds the page called "Qualification
// Matrix". Only this direction needs help: a singular query already matches a
// plural haystack, because "ladder" is a substring of "Ladders".
//
// Deliberately a suffix rule and not a stemmer. A real stemmer would also fold
// "training" → "train" and "requirements" → "requir", which in a corpus of
// department jargon produces confident nonsense; the failure mode here is only
// ever "didn't strip a plural", which is the same as today's behaviour.
const singularOf = token => {
    if (token.length > 4 && token.endsWith('ies')) return `${token.slice(0, -3)}y`;
    if (token.length > 4 && /(ses|xes|ches|shes)$/.test(token)) return token.slice(0, -2);
    if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) return token.slice(0, -1);
    return null;
};

/**
 * The forms of a token worth looking for, most literal first. Callers try them
 * in order so an exact hit always wins over an inflected one.
 */
export const tokenVariants = token => {
    const singular = singularOf(token);
    return singular ? [token, singular] : [token];
};

// Earliest position any variant of `token` occurs at, plus the text that
// actually matched — highlighting needs the matched LENGTH, which differs from
// the typed token when the singular is what hit.
const findToken = (haystack, token) => {
    for (const variant of tokenVariants(token)) {
        const at = haystack.indexOf(variant);
        if (at >= 0) return {at, variant};
    }
    return null;
};

/** A haystack matches when EVERY token is a substring of it (token-AND). */
export const matchesTokens = (haystack, tokens) => tokens.every(token => findToken(haystack, token) !== null);

const lower = value => String(value == null ? '' : value).toLowerCase();

// Field tuples are [label, value, tab] — the tab is where the record opens so a
// hit in an incident's narrative deep-links to the Narrative tab, not the top of
// the record. Only the VALUE is searchable, never the label: matching labels
// made gear's "test" query return every SCBA cylinder just because the category
// had a "Last Hydrostatic Test" field (see gearSearch.js), and the same trap
// applies here — "location" would otherwise match every incident.
const fieldValue = field => (Array.isArray(field) ? field[1] : field?.value);
const fieldLabel = field => (Array.isArray(field) ? field[0] : field?.label);
const fieldTab = field => (Array.isArray(field) ? field[2] : field?.tab);

/**
 * Attach the lowercased haystacks an entry is scored against. The api calls this
 * once at index-build time so a query costs only substring scans; callers that
 * build entries per-render (the client's "Pages" group) can call it inline.
 *
 * Returns a NEW object — entries are shared across tenants' concurrent requests,
 * so nothing here may mutate its input.
 */
export const prepareEntry = entry => {
    const fields = entry.fields || [];
    const fieldTexts = fields.map(field => lower(fieldValue(field)));
    return {
        ...entry,
        lc: {
            title: lower(entry.title),
            subtitle: lower(entry.subtitle),
            typeLabel: lower(entry.typeLabel),
            fields: fieldTexts,
            // One joined haystack so the cheap "does this match at all?" test is
            // a single pass before any scoring arithmetic runs.
            all: [lower(entry.title), lower(entry.subtitle), lower(entry.typeLabel), ...fieldTexts].join(' ')
        }
    };
};

const lcOf = entry => entry.lc || prepareEntry(entry).lc;

// Does `token` start a word in `haystack`? "wong" scores a word hit against
// "Chelsea Wong" but only a substring hit against "Wrongway Rd" — surname and
// street-name matches are both wanted, but the surname is the better answer.
const startsWord = (haystack, token) => {
    let at = haystack.indexOf(token);
    while (at >= 0) {
        if (at === 0 || !/[a-z0-9]/.test(haystack[at - 1])) return true;
        at = haystack.indexOf(token, at + 1);
    }
    return false;
};

const recencyBonus = date => {
    if (!date) return 0;
    const time = date instanceof Date ? date.getTime() : Date.parse(date);
    if (!Number.isFinite(time)) return 0;
    const ageDays = (Date.now() - time) / 86400000;
    if (ageDays <= 0) return RECENCY_MAX;
    // Halves every RECENCY_HALF_LIFE_DAYS, so "this week" and "last week" are
    // near-indistinguishable while "this year" clearly beats "four years ago".
    return RECENCY_MAX / (1 + ageDays / RECENCY_HALF_LIFE_DAYS);
};

/**
 * Score one entry against pre-tokenized query tokens. Returns 0 when any token
 * is missing entirely (token-AND) — a non-zero score always means every token
 * matched somewhere.
 */
export const scoreEntry = (entry, tokens) => {
    if (!tokens.length) return 0;
    const lc = lcOf(entry);

    // Cheap reject first: most entries fail here without any scoring work.
    if (!matchesTokens(lc.all, tokens)) return 0;

    let total = 0;
    for (const rawToken of tokens) {
        let score = 0;

        // Score against whichever form of the token the TITLE carries, so an
        // exact match still outranks one that needed the plural stripped.
        const titleHit = findToken(lc.title, rawToken);
        const token = titleHit?.variant ?? rawToken;

        if (lc.title === token) {
            score += WEIGHT.titleExact;
        } else if (lc.title.startsWith(token)) {
            score += WEIGHT.titlePrefix;
        } else if (startsWord(lc.title, token)) {
            score += WEIGHT.titleWord;
        } else if (titleHit) {
            score += WEIGHT.titleSubstring;
        }

        if (findToken(lc.subtitle, rawToken)) score += WEIGHT.subtitle;
        if (findToken(lc.typeLabel, rawToken)) score += WEIGHT.typeLabel;

        let fieldScore = 0;
        for (const text of lc.fields) {
            if (findToken(text, rawToken)) fieldScore += WEIGHT.field;
            if (fieldScore >= WEIGHT.fieldCap) break;
        }
        score += Math.min(fieldScore, WEIGHT.fieldCap);

        total += titleHit && titleHit.variant !== rawToken ? score * INFLECTED_SCALE : score;
    }

    return total + recencyBonus(entry.date);
};

/**
 * Locate the first token inside `text` and split it for highlighting.
 * Always returns the full text: {pre, hit, post} concatenates back to the input,
 * with `hit` empty when nothing matched.
 */
export const splitHighlight = (text, tokens) => {
    const value = String(text == null ? '' : text);
    const haystack = value.toLowerCase();
    let best = -1;
    let bestLength = 0;
    for (const token of tokens) {
        const found = findToken(haystack, token);
        if (found && (best === -1 || found.at < best)) {
            best = found.at;
            bestLength = found.variant.length;
        }
    }
    if (best === -1) return {pre: value, hit: '', post: ''};
    return {
        pre: value.slice(0, best),
        hit: value.slice(best, best + bestLength),
        post: value.slice(best + bestLength)
    };
};

const SNIPPET_LEAD = 46;
const SNIPPET_TRAIL = 110;

/**
 * Pick the field that best explains why an entry matched, and window it around
 * the hit. Prefers a field matching a token the TITLE does not already show —
 * repeating the title back as its own snippet explains nothing, and the point of
 * the snippet is to answer "why is this in my results?".
 *
 * Returns null when the entry has no fields at all (nav destinations).
 */
export const buildSnippet = (entry, tokens) => {
    const fields = entry.fields || [];
    if (!fields.length) return null;
    const lc = lcOf(entry);

    const unexplained = tokens.filter(token => !findToken(lc.title, token));
    const wanted = unexplained.length ? unexplained : tokens;

    for (const [index, field] of fields.entries()) {
        const value = String(fieldValue(field) ?? '');
        if (!value) continue;
        const haystack = lc.fields[index] ?? value.toLowerCase();
        for (const token of wanted) {
            const found = findToken(haystack, token);
            if (!found) continue;
            const {at, variant} = found;
            const start = Math.max(0, at - SNIPPET_LEAD);
            return {
                label: fieldLabel(field) || '',
                tab: fieldTab(field) || '',
                pre: (start > 0 ? '…' : '') + value.slice(start, at),
                hit: value.slice(at, at + variant.length),
                post: value.slice(at + variant.length, at + variant.length + SNIPPET_TRAIL)
            };
        }
    }

    // Matched on the title alone: show the first field as context rather than
    // leaving the row with nothing under it.
    const first = fields.find(field => fieldValue(field));
    if (!first) return null;
    const value = String(fieldValue(first));
    return {
        label: fieldLabel(first) || '',
        tab: fieldTab(first) || '',
        pre: value.slice(0, SNIPPET_TRAIL),
        hit: '',
        post: ''
    };
};

/**
 * Score, filter and sort a list of entries. Ties break by `typeOrder` (so a
 * mixed page of equally-good matches has a stable, meaningful order) and then by
 * id, so paging can never repeat or skip a row.
 */
export const rankEntries = (entries, tokens, {typeOrder = [], limit} = {}) => {
    if (!tokens.length) return [];
    const rankOf = type => {
        const at = typeOrder.indexOf(type);
        return at === -1 ? typeOrder.length : at;
    };

    const scored = [];
    for (const entry of entries) {
        const score = scoreEntry(entry, tokens);
        if (score > 0) scored.push({entry, score});
    }

    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const typeDiff = rankOf(a.entry.type) - rankOf(b.entry.type);
        if (typeDiff !== 0) return typeDiff;
        return String(a.entry.id) < String(b.entry.id) ? -1 : 1;
    });

    const capped = typeof limit === 'number' ? scored.slice(0, limit) : scored;
    return capped.map(({entry, score}) => ({...entry, score}));
};
